import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import storeLoader from 'app/stores/storeLoader';
import { AiOutlineComment } from 'react-icons/ai';
import { Box, Text, Flex, FlexColumn, Icon, Heading } from 'core/components';
import styled from 'styled-components';
import Comment from './Comment';
import AddEditComment, { AddEditCommentView } from './AddEditComment';
import CommentThreadList from './CommentThreadList';

const CommentWrapper = styled(FlexColumn)`
  padding-bottom: 4px;
  padding-left: ${(props) => (props.integratedMode ? '8px' : '')};
  margin-left: ${(props) => (props.integratedMode ? '18px' : '')};
  overflow: ${(props) => (props.integratedMode ? 'auto' : '')};
  max-height: ${(props) => (props.integratedMode ? '600px' : '')};
`;

@inject('$comments')
@observer
export class CommentListView extends Component {
  static defaultProps = {
    // when put in sidebar, we run it as "integrated", translates into defined maxHeight and adds icon on title
    integratedMode: false,
    // add reply textarea to the end of the list
    addReply: false,
    // 'card' or 'thread': rows with avatars, a sort toggle and the composer below
    variant: 'card'
  };

  state = {
    newestFirst: true
  };

  listRef = React.createRef();

  get commentsForEntity() {
    const { $comments, entityType, entityId, model } = this.props;

    if (entityType === 'alert' && model) {
      return $comments.collection.getAlertComments(model);
    }
    return $comments.collection.getCommentsForEntityById(entityId);
  }

  // Keeps the latest comment in view after the user posts one or flips the sort; refreshes leave the scroll alone.
  scrollToLatest = () => {
    const { newestFirst } = this.state;
    const list = this.listRef.current;

    if (list) {
      list.scrollTop = newestFirst ? 0 : list.scrollHeight;
    }
  };

  handleSortToggle = () => {
    this.setState(({ newestFirst }) => ({ newestFirst: !newestFirst }), this.scrollToLatest);
  };

  get sortedComments() {
    const { newestFirst } = this.state;
    const comments = [...this.commentsForEntity].sort(
      (a, b) => Date.parse(a.get('cdate')) - Date.parse(b.get('cdate'))
    );

    return newestFirst ? comments.reverse() : comments;
  }

  render() {
    const { integratedMode, addReply, entityId, entityType, variant } = this.props;
    const { newestFirst } = this.state;

    if (variant === 'thread') {
      return (
        <CommentThreadList
          comments={this.sortedComments}
          newestFirst={newestFirst}
          listRef={this.listRef}
          onSortToggle={this.handleSortToggle}
          composer={
            addReply && (
              <AddEditCommentView
                variant="thread"
                entityId={entityId}
                entityType={entityType}
                onComment={this.scrollToLatest}
              />
            )
          }
        />
      );
    }

    const comments = this.commentsForEntity;

    return (
      <FlexColumn flex={1} gap={1} maxWidth="600px">
        <Flex>
          {integratedMode && (
            <Box width={26}>
              <Icon icon={AiOutlineComment} iconSize={18} color="muted" />
            </Box>
          )}
          <Heading level={6} mb={0}>
            Comments{' '}
            <Text fontWeight="normal" muted>
              ({comments.length})
            </Text>
          </Heading>
        </Flex>
        {addReply && (
          <div style={{ marginLeft: integratedMode ? '26px' : 0 }}>
            <AddEditComment entityId={entityId} entityType={entityType} />
          </div>
        )}
        {comments.length > 0 && (
          <CommentWrapper integratedMode={integratedMode} gap={1}>
            {comments.map((comment) => (
              <Comment key={comment.id} model={comment} entityType={comment.get('entityType')} />
            ))}
          </CommentWrapper>
        )}
      </FlexColumn>
    );
  }
}

// Loads the company's comments first; pages that load their own entity's comments use CommentListView.
export default storeLoader('$comments')(CommentListView);
